import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Link,
  Chip,
  Tooltip,
  IconButton,
  Collapse
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { simulationService } from '../../services/simulation.service';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const SimulationList = () => {
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRun, setExpandedRun] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSimulations();
  }, []);

  const fetchSimulations = async () => {
    try {
      setLoading(true);
      const data = await simulationService.getSimulationRuns();
      console.log('API Response:', data); // Debug log
      setSimulations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching simulations:', err); // Debug log
      setError(err.message);
      setSimulations([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'running':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getStoreMetrics = (summary) => {
    if (!summary?.store_metrics) return null;
    const metrics = Object.values(summary.store_metrics)[0];
    return metrics ? {
      fillRate: (metrics.fill_rate * 100).toFixed(1) + '%',
      totalOrders: metrics.total_orders,
      totalShipped: metrics.total_shipped
    } : null;
  };

  const getOverallMetrics = (summary) => {
    if (!summary?.overall_metrics) return null;
    const metrics = summary.overall_metrics;
    return {
      totalPurchaseOrders: metrics.total_purchase_orders,
      totalSalesOrders: metrics.total_sales_orders,
      totalOrderLines: metrics.total_order_lines,
      totalShipments: metrics.total_shipments,
      averageFillRate: (metrics.average_fill_rate * 100).toFixed(1) + '%',
      totalLostSales: metrics.total_lost_sales,
      averageLeadTime: metrics.average_lead_time?.toFixed(2),
      totalInventoryValue: metrics.total_inventory_value,
      averageLinesPerSO: metrics.average_lines_per_so?.toFixed(2)
    };
  };

  const toggleExpand = (runId) => {
    setExpandedRun(expandedRun === runId ? null : runId);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Simulation Runs
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Run ID</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Started At</TableCell>
              <TableCell>Completed At</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Metrics</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!simulations || simulations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" color="textSecondary">
                    No simulation runs found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              simulations.map((sim) => {
                const storeMetrics = getStoreMetrics(sim.summary_results);
                const overallMetrics = getOverallMetrics(sim.summary_results);
                const isExpanded = expandedRun === sim.run_id;

                return (
                  <React.Fragment key={sim.run_id}>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={() => toggleExpand(sim.run_id)}
                        >
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{sim.run_id}</TableCell>
                      <TableCell>{sim.description}</TableCell>
                      <TableCell>{formatDate(sim.started_at)}</TableCell>
                      <TableCell>{formatDate(sim.completed_at)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={sim.status} 
                          color={getStatusColor(sim.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {storeMetrics ? (
                          <Tooltip title={
                            <Box>
                              <Typography variant="subtitle2" gutterBottom>Overall Metrics:</Typography>
                              <Typography variant="body2">Fill Rate: {overallMetrics?.fillRate}</Typography>
                              <Typography variant="body2">Total Sales Orders: {overallMetrics?.totalOrders}</Typography>
                              <Typography variant="body2">Total Purchase Orders: {overallMetrics?.totalPurchaseOrders}</Typography>
                              <Typography variant="body2">Total Order Lines: {overallMetrics?.totalOrderLines}</Typography>
                              <Typography variant="body2">Total Shipments: {overallMetrics?.totalShipments}</Typography>
                              <Typography variant="body2">Average Lead Time: {overallMetrics?.averageLeadTime} days</Typography>
                              <Typography variant="body2">Total Inventory Value: ${overallMetrics?.totalInventoryValue}</Typography>
                              <Typography variant="body2">Total Lost Sales: ${overallMetrics?.totalLostSales}</Typography>
                              <Typography variant="body2">Average Lines per SO: {overallMetrics?.averageLinesPerSO}</Typography>
                            </Box>
                          }>
                            <Typography variant="body2">
                              Fill Rate: {storeMetrics.fillRate}
                            </Typography>
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => navigate(`/simulation/map/${sim.run_id}`)}
                        >
                          View Map
                        </Link>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box margin={2}>
                            <Typography variant="h6" gutterBottom component="div">
                              Simulation Details
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Configuration</TableCell>
                                  <TableCell>Overall Metrics</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  <TableCell>
                                    {sim.summary_results?.configuration && (
                                      <Box>
                                        <Typography variant="subtitle2">Shipment Transit Range:</Typography>
                                        <Typography variant="body2">
                                          {sim.summary_results.configuration.shipment_transit_range?.join(' - ')} days
                                        </Typography>
                                      </Box>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {overallMetrics && (
                                      <Box>
                                        <Typography variant="body2">
                                          Fill Rate: {overallMetrics.averageFillRate}
                                        </Typography>
                                        <Typography variant="body2">
                                          Total Sales Orders: {overallMetrics.totalSalesOrders}
                                        </Typography>
                                        <Typography variant="body2">
                                          Total Purchase Orders: {overallMetrics.totalPurchaseOrders}
                                        </Typography>
                                        <Typography variant="body2">
                                          Total Order Lines: {overallMetrics.totalOrderLines}
                                        </Typography>
                                        <Typography variant="body2">
                                          Total Shipments: {overallMetrics.totalShipments}
                                        </Typography>
                                        <Typography variant="body2">
                                          Average Lead Time: {overallMetrics.averageLeadTime} days
                                        </Typography>
                                        <Typography variant="body2">
                                          Total Inventory Value: ${overallMetrics.totalInventoryValue}
                                        </Typography>
                                        <Typography variant="body2">
                                          Total Lost Sales: ${overallMetrics.totalLostSales}
                                        </Typography>
                                        <Typography variant="body2">
                                          Avg Lines per SO: {overallMetrics.averageLinesPerSO}
                                        </Typography>
                                      </Box>
                                    )}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SimulationList; 